import { useState } from 'react'
import { useSelector } from 'react-redux'
import { FiChevronDown, FiChevronUp, FiShoppingBag } from 'react-icons/fi'
import './productComponent.css'

/**
 * ProductCard
 *
 * Props:
 *   name         string     — product name
 *   brand        string?    — brand name
 *   description  string?    — short description / category
 *   ingredients  string[]   — list of ingredient names
 *   quantity     string?    — e.g. "250ml"
 *   price        number     — numeric price
 *   currency     string     — default '€'
 *   imageUrl     string?    — single resolved image URL (from Azure Blob or fallback)
 *   imageLoading boolean?   — show shimmer while parent is still fetching
 *   badge        string?    — optional label e.g. "Recommended"
 *   onAdd        function?  — called when + button clicked
 */
export default function ProductCard({
  name = 'Halisi Product',
  brand,
  description,
  ingredients = [],
  quantity,
  price = 0,
  currency = '€',
  imageUrl,
  imageLoading = false,
  badge,
  onAdd,
}) {
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}
  const [showIngredients, setShowIngredients] = useState(false)
  const [imgError, setImgError] = useState(false)

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language,
  ) => {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  const translateText = (keyPath, replacements = {}) => {
    let value = getTranslationText(keyPath)
    if (typeof value === 'string') {
      Object.entries(replacements).forEach(([key, replacement]) => {
        value = value.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          String(replacement),
        )
      })
    }
    return value
  }

  // Show the image only when we have a URL and no load error
  const showImage = imageUrl && !imgError

  return (
    <>
      <div className='pc-root' tabIndex={0}>
        {/* ── Image ── */}
        <div className='pc-img-wrap'>
          {imageLoading ? (
            <div className='pc-img-loading' />
          ) : showImage ? (
            <img
              src={imageUrl}
              alt={name}
              className='pc-img'
              onError={() => setImgError(true)}
            />
          ) : (
            <div className='pc-img-placeholder'>
              <span className='pc-img-placeholder-icon'>
                <FiShoppingBag />
              </span>
              <span className='pc-img-placeholder-text'>
                {translateText('productCard.imageNotAvailable')}
              </span>
            </div>
          )}

          {badge && <span className='pc-badge'>{badge}</span>}
        </div>

        {/* ── Body ── */}
        <div className='pc-body'>
          {brand && <div className='pc-brand'>{brand}</div>}
          <div className='pc-name'>{name}</div>
          {quantity && <div className='pc-quantity'>{quantity}</div>}
          {description && <div className='pc-desc'>{description}</div>}

          {/* Ingredients toggle — only if list is non-empty */}
          {ingredients.length > 0 && (
            <>
              <button
                className='pc-ingredients-toggle'
                onClick={() => setShowIngredients((v) => !v)}
              >
                {showIngredients ? <FiChevronUp /> : <FiChevronDown />}{' '}
                {translateText('productCard.ingredients')}
              </button>

              {showIngredients && (
                <div className='pc-ingredients-panel'>
                  <strong>{translateText('productCard.keyIngredients')}</strong>
                  {/* Show first 5 to keep the card compact */}
                  {ingredients.slice(0, 5).join(' · ')}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className='pc-footer'>
          <div className='pc-price'>
            <span className='pc-price-currency'>{currency}</span>
            {typeof price === 'number' ? price.toFixed(2) : price}
          </div>
          <button
            className='pc-add-btn'
            onClick={(e) => {
              e.stopPropagation()
              onAdd?.()
            }}
            title={translateText('productCard.addToCart')}
          >
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='12' y1='5' x2='12' y2='19' />
              <line x1='5' y1='12' x2='19' y2='12' />
            </svg>
          </button>
        </div>
      </div>
    </>
  )
}
